import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, Download, Trash2, RefreshCw, LayoutDashboard, Cpu } from "lucide-react";
import { api, formatErr } from "../lib/api";
import PricingAdmin from "../components/PricingAdmin";

const STATUSES = ["all", "new", "contacted", "qualified", "converted", "lost"];

export default function AdminDashboard() {
    const nav = useNavigate();
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [adminName, setAdminName] = useState("");
    const [tab, setTab] = useState("leads");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [leadsRes, statsRes] = await Promise.all([
                api.get("/admin/leads", { params: { status, q: q || undefined } }),
                api.get("/admin/stats"),
            ]);
            setLeads(leadsRes.data);
            setStats(statsRes.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("hol_admin_token");
                nav("/admin/login");
                return;
            }
            toast.error(formatErr(err.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [status, q, nav]);

    useEffect(() => {
        if (!localStorage.getItem("hol_admin_token")) {
            nav("/admin/login");
            return;
        }
        setAdminName(localStorage.getItem("hol_admin_name") || "Admin");
        load();
    }, [load, nav]);

    const logout = () => {
        localStorage.removeItem("hol_admin_token");
        localStorage.removeItem("hol_admin_name");
        nav("/admin/login");
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await api.patch(`/admin/leads/${id}`, { status: newStatus });
            toast.success("Status updated");
            load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            await api.delete(`/admin/leads/${id}`);
            toast.success("Lead deleted");
            load();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        }
    };

    const exportCsv = () => {
        if (!leads.length) return toast.error("No leads to export");
        const headers = ["Name", "Email", "Phone", "City", "Budget", "Property Type", "Message", "Status", "Created"];
        const rows = leads.map((l) => [l.name, l.email, l.phone, l.city, l.budget || "", l.property_type || "", (l.message || "").replace(/"/g, '""'), l.status, l.created_at]);
        const csv = [headers, ...rows].map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `lead-krafts-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#050507] text-white">
            <header className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <img src="/lead-krafts-icon.png" alt="Lead Krafts" className="h-9 w-9 object-contain" />
                        <div>
                            <div className="overline !text-white/40">Admin Console</div>
                            <div className="font-serif-display text-lg">Lead Krafts</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-white/60 hidden sm:block">
                            Signed in as <span className="text-[#D4AF37]">{adminName}</span>
                        </div>
                        <button data-testid="admin-logout" onClick={logout} className="btn-outline-gold text-sm">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                <div className="mb-8">
                    <h1 className="font-serif-display text-4xl sm:text-5xl">Admin <span className="italic text-[#D4AF37]">Console</span></h1>
                    <p className="mt-3 text-white/60">Manage leads pipeline and the AI pricing engine.</p>
                </div>

                <div className="flex gap-2 mb-8 border-b border-white/10">
                    <button data-testid="tab-leads" onClick={() => setTab("leads")} className={`px-4 py-2.5 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px ${tab === "leads" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50 hover:text-white"}`}>
                        <LayoutDashboard size={12} /> Leads
                    </button>
                    <button data-testid="tab-pricing" onClick={() => setTab("pricing")} className={`px-4 py-2.5 text-xs uppercase tracking-widest flex items-center gap-2 border-b-2 -mb-px ${tab === "pricing" ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/50 hover:text-white"}`}>
                        <Cpu size={12} /> AI Pricing
                    </button>
                </div>

                {tab === "pricing" ? <PricingAdmin /> : (<><div className="mb-10"></div></>)}
                {tab === "leads" && (<></>)}

                {stats && (
                    <div className={`grid grid-cols-2 md:grid-cols-5 gap-4 mb-10 ${tab !== "leads" ? "hidden" : ""}`} data-testid="stats-grid">
                        <StatTile label="Total" value={stats.total} highlight />
                        <StatTile label="New" value={stats.new} />
                        <StatTile label="Contacted" value={stats.contacted} />
                        <StatTile label="Qualified" value={stats.qualified} />
                        <StatTile label="Converted" value={stats.converted} />
                    </div>
                )}

                <div className={`bg-[#0A0B10] border border-white/10 ${tab !== "leads" ? "hidden" : ""}`}>
                    <div className="p-6 border-b border-white/10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                        <div className="flex flex-wrap gap-2">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    data-testid={`filter-${s}`}
                                    onClick={() => setStatus(s)}
                                    className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                                        status === s
                                            ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                                            : "border-white/10 text-white/50 hover:border-white/30"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                                <input
                                    data-testid="search-input"
                                    className="hol-input pl-9 !py-2 w-64"
                                    placeholder="Search name, email, city..."
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && load()}
                                />
                            </div>
                            <button data-testid="refresh-btn" onClick={load} className="p-2 border border-white/10 hover:border-[#D4AF37] text-white/70 hover:text-[#D4AF37]">
                                <RefreshCw size={14} />
                            </button>
                            <button data-testid="export-csv" onClick={exportCsv} className="btn-outline-gold text-sm">
                                <Download size={14} /> CSV
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="p-12 text-center text-white/40">Loading leads...</div>
                        ) : leads.length === 0 ? (
                            <div className="p-12 text-center text-white/40" data-testid="empty-state">No leads yet.</div>
                        ) : (
                            <table className="w-full text-sm" data-testid="leads-table">
                                <thead className="border-b border-white/10 text-white/50 text-xs uppercase tracking-widest">
                                    <tr>
                                        <th className="text-left p-4">Lead</th>
                                        <th className="text-left p-4">Contact</th>
                                        <th className="text-left p-4">City</th>
                                        <th className="text-left p-4">Budget</th>
                                        <th className="text-left p-4">Status</th>
                                        <th className="text-left p-4">Created</th>
                                        <th className="text-right p-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l) => (
                                        <tr key={l.id} className="border-b border-white/5 hover:bg-white/2" data-testid={`lead-row-${l.id}`}>
                                            <td className="p-4">
                                                <div className="font-serif-display text-base">{l.name}</div>
                                                <div className="text-xs text-white/40 mt-0.5">{l.property_type || "—"}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-white/80">{l.email}</div>
                                                <div className="text-xs text-white/40 mt-0.5">{l.phone}</div>
                                            </td>
                                            <td className="p-4 text-white/70">{l.city}</td>
                                            <td className="p-4 text-white/70">{l.budget || "—"}</td>
                                            <td className="p-4">
                                                <select
                                                    data-testid={`status-select-${l.id}`}
                                                    value={l.status}
                                                    onChange={(e) => updateStatus(l.id, e.target.value)}
                                                    className="bg-black/50 border border-white/10 text-xs px-2 py-1 text-white uppercase tracking-widest focus:border-[#D4AF37] outline-none"
                                                >
                                                    {STATUSES.filter((s) => s !== "all").map((s) => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="p-4 text-white/50 text-xs">
                                                {new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                            </td>
                                            <td className="p-4 text-right">
                                                <button
                                                    data-testid={`delete-${l.id}`}
                                                    onClick={() => remove(l.id)}
                                                    className="text-white/40 hover:text-red-400 p-2"
                                                    aria-label="delete"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}

function StatTile({ label, value, highlight }) {
    return (
        <div className={`p-6 border ${highlight ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-white/10 bg-[#0A0B10]"}`}>
            <div className="overline !text-white/40">{label}</div>
            <div className="font-serif-display text-4xl mt-2 text-white">{value}</div>
        </div>
    );
}
